import { useEffect, useState } from 'react';
import { z } from 'zod';
import { firestore } from '../firebase';
import { useAppSelector } from '../hooks';

export const offerAccessSchema = z.object({
  version: z.number().int().nonnegative(),
  active: z.boolean(),
  canCreate: z.boolean(),
  allRead: z.boolean(),
  allEdit: z.boolean(),
  allDelete: z.boolean(),
  allExport: z.boolean(),
  readOwnerIds: z.array(z.string()),
  editOwnerIds: z.array(z.string()),
  deleteOwnerIds: z.array(z.string()),
  exportOwnerIds: z.array(z.string()),
});
export type OfferAccess = z.infer<typeof offerAccessSchema>;
export type OfferAction = 'read' | 'edit' | 'delete' | 'export';
export function legacyOfferAccess(uid: string): OfferAccess {
  const administrator = ['APjQ0ab0pPZFCiRvcWu1TGwoKBE2', '4BESrD32e6MhNat2TolxXFFH3il1'].includes(
    uid
  );
  const canCreate =
    administrator ||
    [
      'pLJE7dQkMTYMoB7mRvPx70utopV2',
      'S7S6HNQpw9SWprZfQ3pUSWNPp4A3',
      '7k7PPZBo6aRsvUhMkCRYwE89CGI3',
      'xCoOswPjxUMmV3GkQqE6qVSrT5f2',
      'O3hQgV5PMlVx3Xlk36o3ITRyNOZl',
    ].includes(uid);
  return {
    version: 0,
    active: true,
    canCreate,
    allRead: administrator,
    allEdit: administrator,
    allDelete: administrator,
    allExport: administrator,
    readOwnerIds: [uid],
    editOwnerIds: [uid],
    deleteOwnerIds: [uid],
    exportOwnerIds: [uid],
  };
}
export function canAccessOffer(
  policy: OfferAccess | null,
  ownerId: unknown,
  action: OfferAction = 'read'
) {
  if (!policy?.active || typeof ownerId !== 'string' || !ownerId) return false;
  const readable = policy.allRead || policy.readOwnerIds.includes(ownerId);
  return (
    readable &&
    (action === 'read' ||
      (action === 'edit' && (policy.allEdit || policy.editOwnerIds.includes(ownerId))) ||
      (action === 'delete' && (policy.allDelete || policy.deleteOwnerIds.includes(ownerId))) ||
      (action === 'export' && (policy.allExport || policy.exportOwnerIds.includes(ownerId))))
  );
}

export default function useOfferAccess(enabled = true) {
  const uid = useAppSelector((state) => state.user.details.uid);
  const [state, setState] = useState<{
    uid: string;
    policy: OfferAccess | null;
    error: Error | null;
  }>();
  useEffect(() => {
    setState(undefined);
    if (!uid || !enabled) return;
    let active = true;
    const unsubscribe = firestore
      .collection('crmOfferAccess')
      .doc(uid)
      .onSnapshot(
        (snapshot) => {
          if (!active) return;
          const parsed = offerAccessSchema.safeParse(
            snapshot.exists ? snapshot.data() : legacyOfferAccess(uid)
          );
          setState({
            uid,
            policy: parsed.success ? parsed.data : null,
            error: parsed.success
              ? null
              : new Error('Tilbudsadgangen skal gennemgås af en administrator.'),
          });
        },
        (error) => {
          if (active) setState({ uid, policy: null, error });
        }
      );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [uid, enabled]);
  const current = enabled && state?.uid === uid ? state : undefined;
  return {
    uid,
    policy: current?.policy || null,
    loading: Boolean(enabled && uid && !current),
    error: current?.error || null,
  };
}
